import React, { createContext, useContext, useState, type ReactNode, useCallback } from 'react';
import Alert, { type AlertType } from '../components/base/Alert';

interface AlertContextProps {
  showAlert: (message: string, type?: AlertType, options?: AlertOptions) => void;  // ⭐ type?: AlertType
  hideAlert: () => void;
}

interface AlertOptions {
  onRetry?: () => void;
  autoClose?: boolean;
  duration?: number;
}

interface AlertState {
  isOpen: boolean;
  message: string;
  type: AlertType;
  onRetry?: () => void;
  autoClose: boolean;
  duration: number;
}

const initialAlertState: AlertState = {
  isOpen: false,
  message: '',
  type: 'info',
  autoClose: false,
  duration: 5000
};

const AlertContext = createContext<AlertContextProps | undefined>(undefined);

export const AlertProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [alertState, setAlertState] = useState<AlertState>(initialAlertState);

  const showAlert = useCallback((message: string, type?: AlertType, options?: AlertOptions) => {
    setAlertState({
      isOpen: true,
      message,
      type: type ?? 'info',
      onRetry: options?.onRetry,
      autoClose: options?.autoClose ?? false,
      duration: options?.duration ?? 5000
    });
  }, []);

  const hideAlert = useCallback(() => {
    setAlertState(prev => ({ ...prev, isOpen: false }));
  }, []);

  return (
    <AlertContext.Provider value={{ showAlert, hideAlert }}>
      {children}
      <Alert
        type={alertState.type}
        message={alertState.message}
        isOpen={alertState.isOpen}
        onClose={hideAlert}
        onRetry={alertState.onRetry}
        autoClose={alertState.autoClose}
        duration={alertState.duration}
      />
    </AlertContext.Provider>
  );
};

export const useAlert = (): AlertContextProps => {
  const context = useContext(AlertContext);
  if (context === undefined) {
    throw new Error('useAlert must be used within a AlertProvider');
  }
  return context;
};